import React, { useEffect, useState } from 'react'
import SessionItem from 'theme/components/organisms/agenda/session-item'
import ApiGateway from 'theme/api/api-gateway'
import { useFirebaseService } from 'theme/services/firebase-service'
import PropTypes from 'prop-types'
import withFeedbackModal from 'theme/hoc/withFeedbackModal'
import { orderBy } from 'lodash-es'
import withToggleFavouriteSession from 'theme/hoc/withToggleFavouriteSession'
// eslint-disable-next-line import/named
import { useAllUserSessions } from 'src/hooks/userAllUserSessions'
import { getConferenceLocation } from 'theme/utils/generic-helpers'
import { SquareArticleCard } from 'src/components/articleCard'
import { off } from 'firebase/database'

const Sessions = ({ user, openFeedbackModal, toggleFavourite, sessions, articles, person, isAgenda }) => {
  const [allSessionsList, setAllSessionList] = useState(orderBy(sessions, 'data.UTC_Start_Date', 'desc'))
  const [showMorePastSessions, setShowMorePastSessions] = useState(false)
  const [showMoreArticles, setShowMoreArticles] = useState(false)
  const [getRegistrations] = useAllUserSessions()
  const firebase = useFirebaseService()
  const now = Date.now()
  const pastSessions = allSessionsList.filter((session) => {
    if (session.data.Video || session.data.Always_Display_on_Speaker_Page)
      return new Date(session.data.UTC_Start_Time).getTime() <= now
  })
  const displayPastSessions = pastSessions.slice(0, 3)
  const otherPastSessions = pastSessions.slice(3, pastSessions.length)

  const futureSessions = allSessionsList
    .filter((session) => {
      return new Date(session.data.UTC_Start_Time).getTime() > now
    })
    .sort((a, b) => {
      return new Date(a.data.UTC_Start_Time).getTime() - new Date(b.data.UTC_Start_Time).getTime()
    })

  const displayArticles = articles.slice(0, 3)
  const otherArticles = articles.slice(3, articles.length)
  // console.log(otherArticles)

  useEffect(() => {
    if (!user) {
      return
    }

    const favouriteSessionsDBRef = ApiGateway.observeAllFavouriteSessions(firebase, user.uid, (values) => {
      const favouriteSessionsIds = values.map((item) => item.sessionId)

      setAllSessionList((prevList) => {
        return prevList.map((session) => {
          session.isFavourite = favouriteSessionsIds.includes(session.id)
          return session
        })
      })
    })

    return () => {
      off(favouriteSessionsDBRef)
    }
  }, [user])

  return (
    <section className="max-w-screen-xl mb-12 mt-10 m-auto px-2">
      {futureSessions.length > 0 && <h2 className="text-4xl mb-4">Upcoming Talks</h2>}
      {futureSessions.map((session) => {
        const conferenceLocation = getConferenceLocation(session.data.Conference[0])
        const conferenceId = session.data.Conference[0].data.Conference_Id
        const registrations = getRegistrations(conferenceId)
        const userUsedPlaces = registrations.length

        return (
          <SessionItem
            key={session.id}
            userUsedPlaces={userUsedPlaces}
            registrations={registrations}
            session={session}
            toggleFavourite={toggleFavourite}
            openFeedbackModal={openFeedbackModal}
            conferenceLocation={conferenceLocation}
            conferenceId={conferenceId}
            conferenceVenueType={session.data.Conference[0].data.Conference_Venue_Type}
          />
        )
      })}

      {displayPastSessions.length > 0 && (
        <>
          <h2 className="text-4xl mb-4">Past Talks</h2>
          {displayPastSessions.map((session) => {
            const conferenceLocation = getConferenceLocation(session.data.Conference?.[0])
            const conferenceId = session.data.Conference?.[0]?.data?.Conference_Id
            const registrations = conferenceId && getRegistrations(conferenceId)
            const userUsedPlaces = registrations?.length

            return (
              <SessionItem
                key={session.id}
                userUsedPlaces={userUsedPlaces}
                registrations={registrations}
                session={session}
                toggleFavourite={toggleFavourite}
                openFeedbackModal={openFeedbackModal}
                conferenceLocation={conferenceLocation}
                conferenceId={conferenceId}
                conferenceVenueType={session.data.Conference?.[0]?.data?.Conference_Venue_Type}
              />
            )
          })}
        </>
      )}
      {otherPastSessions.length > 0 && !showMorePastSessions && (
        <button
          onClick={() => setShowMorePastSessions(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {' '}
          Show More{' '}
        </button>
      )}
      {showMorePastSessions &&
        otherPastSessions.map((session) => {
          const conferenceLocation = getConferenceLocation(session.data.Conference?.[0])
          const conferenceId = session.data.Conference?.[0]?.data?.Conference_Id
          const registrations = conferenceId && getRegistrations(conferenceId)
          const userUsedPlaces = registrations?.length

          return (
            <SessionItem
              key={session.id}
              userUsedPlaces={userUsedPlaces}
              registrations={registrations}
              session={session}
              toggleFavourite={toggleFavourite}
              openFeedbackModal={openFeedbackModal}
              conferenceLocation={conferenceLocation}
              conferenceId={conferenceId}
              conferenceVenueType={session.data.Conference?.[0]?.data?.Conference_Venue_Type}
            />
          )
        })}

      {displayArticles.length > 0 && (
        <>
          <h2 className="text-4xl mb-4 mt-12">Articles</h2>
          <div className="mt-12 max-w-lg mx-auto grid gap-5 md:grid-cols-2 lg:grid-cols-3 lg:max-w-none mb-8">
            {displayArticles.map((post) => {
              return (
                <div key={post.id} className="flex flex-col rounded-lg shadow-lg overflow-hidden col-span-1">
                  <SquareArticleCard post={post} authors={post.data.Author} />
                </div>
              )
            })}
          </div>
        </>
      )}
      {otherArticles.length > 0 && !showMoreArticles && (
        <button
          onClick={() => setShowMoreArticles(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          {' '}
          Show More{' '}
        </button>
      )}
      {showMoreArticles && otherArticles.length > 0 && (
        <>
          <div className="mt-12 max-w-lg mx-auto grid gap-5 md:grid-cols-2 lg:grid-cols-3 lg:max-w-none">
            {otherArticles.map((post) => {
              const authors = [person]
              return (
                <div
                  key={post.data.Ghost_Slug}
                  className="flex flex-col rounded-lg shadow-lg overflow-hidden col-span-1"
                >
                  <SquareArticleCard post={post} authors={authors} />
                </div>
              )
            })}
          </div>
        </>
      )}
    </section>
  )
}

Sessions.propTypes = {
  user: PropTypes.object,
  sessions: PropTypes.array.isRequired,
  openFeedbackModal: PropTypes.func.isRequired,
  toggleFavourite: PropTypes.func.isRequired,
  articles: PropTypes.array,
  person: PropTypes.object.isRequired,
}

export default withFeedbackModal(withToggleFavouriteSession(Sessions))
